/**
 * Cross-margin socket trios (buy.max / sell.max) are computed at account max leverage.
 * Scale down to the user's selected leverage for display, sliders, and validation.
 *
 * max(L) = available + (maxAtMaxLev - available) * (L - 1) / (maxLev - 1)
 */
export function getCrossMarginMaxLeverage({ marginBalance, selectedCoin, currentMarginAccount }) {
    const allowed = selectedCoin?.margin_config?.cross_allowed_leverages;
    if (Array.isArray(allowed) && allowed.length > 0) {
        const nums = allowed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
        if (nums.length) return Math.max(...nums);
    }
    const fromSocket = marginBalance?.maxLeverageSocket;
    if (fromSocket != null && Number.isFinite(Number(fromSocket)) && Number(fromSocket) > 0) {
        return Number(fromSocket);
    }
    return (
        Number(currentMarginAccount?.max_leverage)
        || Number(selectedCoin?.margin_config?.max_leverage)
        || 5
    );
}

export function crossMarginMaxAtLeverage(available, maxAtMaxLeverage, selectedLeverage, maxLeverage) {
    const avail = Number(available);
    const maxAtMax = Number(maxAtMaxLeverage);
    const L = Number(selectedLeverage);
    const M = Number(maxLeverage);

    if (!Number.isFinite(avail) || avail < 0) return 0;
    if (!Number.isFinite(maxAtMax) || maxAtMax <= 0) return Math.max(0, avail);
    if (!Number.isFinite(L) || L <= 0) return Math.max(0, avail);
    if (!Number.isFinite(M) || M <= 1) return Math.max(0, Math.min(maxAtMax, avail));
    if (L >= M) return Math.max(0, maxAtMax);
    if (L <= 1) return Math.max(0, avail);

    const borrowable = Math.max(0, maxAtMax - avail);
    return Math.max(0, avail + borrowable * ((L - 1) / (M - 1)));
}

export function crossMarginBuyMaxAtLeverage(marginBalance, selectedLeverage, maxLeverage) {
    if (marginBalance?.buyMax == null) return 0;
    const available = marginBalance.buyAvailable != null
        ? parseFloat(marginBalance.buyAvailable)
        : parseFloat(marginBalance.buyMax);
    return crossMarginMaxAtLeverage(
        available,
        parseFloat(marginBalance.buyMax),
        selectedLeverage,
        maxLeverage
    );
}

export function crossMarginSellMaxAtLeverage(marginBalance, selectedLeverage, maxLeverage) {
    if (marginBalance?.sellMax == null) return 0;
    const available = marginBalance.sellAvailable != null
        ? parseFloat(marginBalance.sellAvailable)
        : parseFloat(marginBalance.sellMax);
    return crossMarginMaxAtLeverage(
        available,
        parseFloat(marginBalance.sellMax),
        selectedLeverage,
        maxLeverage
    );
}
